import { accordionRecipe } from '@/theme';
import { Accordion, Card, HStack, useSlotRecipe } from '@chakra-ui/react';
import React from 'react';
import { LuChevronDown } from 'react-icons/lu';

export const AccordionRoot = Accordion.Root;

type MyAccordionItemProps = React.ComponentProps<typeof Accordion.Item> & {
  children: React.ReactNode;
};

export function AccordionItem({ children, value, ...rest }: MyAccordionItemProps) {
  return (
    <Card.Root variant="elevated" background="navButtonDefault" width="100%">
      <Card.Body p={0} background="none">
        <Accordion.Item value={value} {...rest} border="none">
          {children}
        </Accordion.Item>
      </Card.Body>
    </Card.Root>
  );
}

// Derive from the actual body component
type AccordionItemContentProps = React.ComponentProps<typeof Accordion.ItemBody>;

export const AccordionItemContent = React.forwardRef<HTMLDivElement, AccordionItemContentProps>(
  function AccordionItemContent(props, ref) {
    const recipe = useSlotRecipe({ recipe: accordionRecipe });
    const style = recipe();

    return (
      <Accordion.ItemContent css={style.itemContent} background="transparent">
        <Accordion.ItemBody {...props} ref={ref} />
      </Accordion.ItemContent>
    );
  },
);

// Derive props from the real subcomponent
type AccordionItemTriggerProps = React.ComponentProps<typeof Accordion.ItemTrigger>;

export const AccordionItemTrigger = React.forwardRef<HTMLButtonElement, AccordionItemTriggerProps>(
  function AccordionItemTrigger(props, ref) {
    const { children, indicatorPlacement = 'end', color, ...rest } = props;
    const recipe = useSlotRecipe({ recipe: accordionRecipe });
    const style = recipe();

    return (
      <Accordion.ItemTrigger
        background="navButtonDefault"
        {...rest}
        ref={ref}
        css={style.itemTrigger}
      >
        {indicatorPlacement === 'start' && (
          <Accordion.ItemIndicator color={color}>
            <LuChevronDown />
          </Accordion.ItemIndicator>
        )}

        <HStack gap="4" flex="1" textAlign="start" width="full">
          {children}
        </HStack>

        {indicatorPlacement === 'end' && (
          <Accordion.ItemIndicator color={color}>
            <LuChevronDown />
          </Accordion.ItemIndicator>
        )}
      </Accordion.ItemTrigger>
    );
  },
);
